import {ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormArray, NonNullableFormBuilder} from '@angular/forms';
import {BehaviorSubject, filter, firstValueFrom, map, Observable, of, startWith, Subject, throwError} from 'rxjs';
import {switchMap, takeUntil} from 'rxjs/operators';
import {FileUploadComponent} from '../../../shared/components/controls/ui-controls/upload/file-upload.component';
import {UploadFile, UploadFileStatusEnum} from '../../../shared/components/controls/ui-controls/upload/upload-file';
import {UploadService} from '../../../core/services/apiservices/upload.service';
import {UploadFileFG} from '../../../core/model/nachricht-form';
import {HinweisboxTypeEnum} from '../../../shared/components/hinweisbox/hinweisbox-type.enum';
import {HinweisboxComponent} from '../../../shared/components/hinweisbox/hinweisbox.component';
import {AsyncPipe} from '@angular/common';
import {OrdnerEnum} from '../../../core/model/ordner.enum';
import {AnhangDownloadService} from '../../../core/services/apiservices/anhang-download.service';
import {DownloadHelperService} from '../../../core/services/download-helper.service';
import {AnhangUploadStatusEnum} from '../../../core/model/upload-status';
import {environment} from "../../../../environments/environment";
import {FileUploadFacade} from "./file-upload.fascade";
import {ErrorMessageService} from "../../../core/services/error-message.service";
import {LoggingService} from "../../../core/services/logging.service";
import {I18NextPipe} from "@kokos-i18next";

@Component({
    selector: 'kokos-nachricht-file-uploader',
    standalone: true,
    templateUrl: './nachricht-file-uploader.component.html',
    imports: [FileUploadComponent, HinweisboxComponent, AsyncPipe, I18NextPipe],
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class NachrichtFileUploaderComponent implements OnDestroy, OnInit {
    @Input() anhaengeFormArray!: FormArray<UploadFileFG>;
    @Input() name: string;

    private readonly nachrichtId$ = new BehaviorSubject<string | null>(null);

    @Input()
    set nachrichtId(id: string | null) {
        this.nachrichtId$.next(id);
    }

    get nachrichtId(): string {
        return this.nachrichtId$.value;
    }

    @Output() requestCreateEntwurf = new EventEmitter<void>();
    @Output() uploading = new EventEmitter<void>();
    @Output() allFilesUploaded = new EventEmitter<void>();

    private readonly uploadService = inject(UploadService);
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly anhangDownloadService = inject(AnhangDownloadService);
    private readonly downloadHelperService = inject(DownloadHelperService);

    private readonly facade = inject(FileUploadFacade);
    private readonly errorMessageService= inject(ErrorMessageService);
    private readonly loggingService= inject(LoggingService);

    displayFiles$!: Observable<UploadFile[]>;
    private readonly destroy$ = new Subject<void>();

    protected readonly HinweisboxTypeEnum = HinweisboxTypeEnum;
    protected readonly environment = environment;
    showError = false;
    emittedAllDone = false;

    ngOnInit() {
        this.displayFiles$ = this.anhaengeFormArray.valueChanges.pipe(
            startWith(this.anhaengeFormArray.getRawValue()),
            map(result => result as UploadFile[]),
            takeUntil(this.destroy$)
        );
    }

    async onFilesSelected(files: FileList) {
        this.uploading.emit();

        this.showError = false;
        const originals = Array.from(files);

        if (!this.nachrichtId) {
            this.requestCreateEntwurf.emit();
            await firstValueFrom(
                this.nachrichtId$.pipe(
                    filter((v): v is string => !!v),
                    takeUntil(this.destroy$)
                )
            );
        }

        // Sanitization, Validation & FormGroups
        this.facade
            .processFiles(originals)
            .pipe(takeUntil(this.destroy$))
            .subscribe(items => {
                for (const {formGroup} of items) {
                    this.anhaengeFormArray.push(formGroup);
                }
                this.checkAndEmitAllDone();
                for (const {file, formGroup, isValid} of items) {
                    if (isValid) {
                        this.startUpload(file, formGroup);
                    }
                }
            });

    }


    removeFile(index: number): void {
        this.showError = false;
        const group = this.anhaengeFormArray.at(index);
        const anhangId = group.controls.anhangId.value;
        if (!anhangId) {
            this.anhaengeFormArray.removeAt(index);
            return;
        }

        this.uploadService.deleteFile(this.nachrichtId, anhangId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => this.anhaengeFormArray.removeAt(index),
                error: () => this.showError = true
            });
    }


    onOpenFile(index: number): void {
        const group = this.anhaengeFormArray.at(index);
        const anhangId = group.controls.anhangId.value;
        const fileName = group.controls.fileName.value;

        this.anhangDownloadService.fetchDownload(OrdnerEnum.ENTWURF, this.nachrichtId, anhangId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: blob => this.downloadHelperService.downloadBlob(blob, fileName, true),
                error: () => console.log('could not open file')
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }


    private startUpload(file: File, fg: UploadFileFG): void {

        //file hochladen
        this.uploadService.uploadFile(this.nachrichtId, file).pipe(

            // status pollen
            switchMap((anhangId: string) => {
                    // remember anhangId at first possible iteration so it can be cancelled or deleted if unsuccessful
                    if (!fg.value.anhangId) {
                        fg.patchValue({
                            anhangId: anhangId,
                        });
                    }
                    return this.uploadService.pollUploadStatus(this.nachrichtId, anhangId).pipe(
                        map(statusResp => ({statusResp, anhangId}))
                    )
                }
            ),

            // metadaten holen
            switchMap(({statusResp, anhangId}) => {
                if (statusResp.status === AnhangUploadStatusEnum.ZUGEORDNET) {
                    return this.uploadService.getAnhangMetadaten(this.nachrichtId, anhangId);
                }
                if (statusResp.status === AnhangUploadStatusEnum.FEHLER) {
                    return throwError(() => statusResp);
                }
                return of(null);
            }),

            takeUntil(this.destroy$)

        ).subscribe({
            // form aktualisieren
            next: metadaten => {
                if (metadaten) {
                    fg.patchValue({
                        anhangId: metadaten.anhangId,
                        size:metadaten.groesse,
                        fileName: metadaten.dateiname,
                        status: UploadFileStatusEnum.SUCCESS
                    });
                } else {
                    fg.patchValue({
                        status: UploadFileStatusEnum.ERROR,
                        errorKey: this.errorMessageService.getErrorKey('error.upload.generic')
                    });
                }
                this.checkAndEmitAllDone();
            },
            error: (error) => {
                const errorText = this.errorMessageService.getErrorKey(error.error??error);
                fg.patchValue({
                    status: UploadFileStatusEnum.ERROR,
                    errorKey: errorText
                });
                this.loggingService.info({...error, 'origin': 'upload', errorText });
                this.checkAndEmitAllDone();
            }
        });
    }


    private checkAndEmitAllDone(): void {
        const controls = this.anhaengeFormArray?.controls ?? [];
        if (!controls || controls.length === 0) {
            this.allFilesUploaded.emit();
            return;
        }
        const hasLoading = controls.some(c => {
            const s = c.get('status')?.value;
            return s === UploadFileStatusEnum.LOADING || s == null;
        });

        if (!hasLoading) {
            this.allFilesUploaded.emit();
        }
    }
}